import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchReglasNivel,
    ReglaNivelUsuario
} from '../../../services/reglasNivelUsuariosService';
import AddReglasNivelPage from '../../../components/Reglas_nivel/AddReglasNivelPage';
import EditReglasNivelPage from '../../../components/Reglas_nivel/EditReglasNivelPage';
import DeleteReglasNivelPage from '../../../components/Reglas_nivel/DeleteReglasNivelPage';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission'; 

const sortByPriorityDesc = (a: ReglaNivelUsuario, b: ReglaNivelUsuario) => b.prioridad - a.prioridad;

const ReglasNivelPage: React.FC = () => {
    const [reglas, setReglas] = useState<ReglaNivelUsuario[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRegla, setSelectedRegla] = useState<ReglaNivelUsuario | null>(null);

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth(); // 👈

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('reglanivelusuario', userLevelId);

    const loadReglas = useCallback(async () => {
        if (!perm?.can_read) {
            setReglas([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await fetchReglasNivel();
            const sortedData = [...data].sort(sortByPriorityDesc);
            setReglas(sortedData);
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm?.can_read]);

    useEffect(() => {
        loadReglas();
    }, [loadReglas]);

    const totalPages = Math.ceil(reglas.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedReglas = reglas.slice(startIndex, startIndex + itemsPerPage);

    const handleReglaCreated = (newRegla: ReglaNivelUsuario) => {
        setReglas(prev => [...prev, newRegla].sort(sortByPriorityDesc));
    };

    const handleReglaUpdated = (updatedRegla: ReglaNivelUsuario) => {
        setReglas(prev =>
            prev.map(r => (r.id === updatedRegla.id ? updatedRegla : r)).sort(sortByPriorityDesc)
        );
    };

    const handleReglaDeleted = (deletedId: number) => {
        setReglas(prev => prev.filter(r => r.id !== deletedId));
    };

    const handleCreate = () => setShowAddModal(true);
    const handleEdit = (regla: ReglaNivelUsuario) => {
        setSelectedRegla(regla);
        setShowEditModal(true);
    };
    const handleDeleteClick = (regla: ReglaNivelUsuario) => {
        setSelectedRegla(regla);
        setShowDeleteModal(true);
    };

    const formatCurrency = (value: string | null) => {
        if (!value) return '—';
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB' }).format(num);
    };

    if (perm === null) return <Loading text="Cargando reglas de nivel..." />;

    return <><LumaResourceWorkspace title="Reglas de nivel" description="Consulta y gestiona la información de tu coworking." group="Usuarios" rows={reglas} columns={[{key:'nombre',label:'Nombre'},{key:'nivel_destino_nombre',label:'Nivel destino'},{key:'prioridad',label:'Prioridad'},{key:'min_citas',label:'Mínimo de citas'},{key:'activo',label:'Estado'}]} idKey="id" nameKey="nombre" statusKey="activo" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setShowAddModal(true)}:undefined} onEdit={perm?.can_update?handleEdit:undefined} onDelete={perm?.can_delete?handleDeleteClick:undefined} history=""/><ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />{showAddModal && (<AddReglasNivelPage
                        onClose={() => setShowAddModal(false)}
                        onCreated={handleReglaCreated}
                    />)}{showEditModal && selectedRegla && (<EditReglasNivelPage
                        regla={selectedRegla}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedRegla(null);
                        }}
                        onUpdated={handleReglaUpdated}
                    />)}{showDeleteModal && selectedRegla && (<DeleteReglasNivelPage
                        regla={selectedRegla}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setSelectedRegla(null);
                        }}
                        onDeleted={handleReglaDeleted}
                    />)}</>;
};

export default ReglasNivelPage;
